import { useEffect, useState } from "react";
import { DropZone } from "@/components/DropZone";
import { api, euro, fmtDate, downloadReport, fetchAuthBlobUrl, formatApiErrorDetail, openAuthPdf } from "@/lib/api";
import { paymentTermLabel } from "@/lib/constants";
import { PortalLayout } from "@/components/PortalLayout";
import { useLang, translate } from "@/lib/i18n";
import { StatusBadge, TypeBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileCsvIcon as FileCsv, FileXlsIcon as FileXls, FilePdfIcon as FilePdf, GaugeIcon as Gauge, PlusIcon as Plus, ChatCircleDotsIcon as ChatCircleDots, TicketIcon as TicketIcon, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ShieldCheckIcon as ShieldCheck, PaperclipIcon as Paperclip } from "@phosphor-icons/react";

const HERO = "/brand/team.jpg";

function H({ title, subtitle }) {
  const lang = useLang();
  return (
    <div className="mb-8">
      <h1 className="font-heading text-3xl font-bold tracking-tight">{translate(title, lang)}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{translate(subtitle, lang)}</p>}
    </div>
  );
}

export function PortalHome() {
  const [profile, setProfile] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    api.get("/portal/profile").then(({ data }) => setProfile(data));
    api.get("/portal/contracts").then(({ data }) => setContracts(data));
    api.get("/portal/devices").then(({ data }) => setDevices(data));
  }, []);

  const p = profile || {};
  const rows = [
    ["Ragione sociale", p.ragione_sociale],
    ["Partita IVA", p.partita_iva],
    ["Codice fiscale", p.codice_fiscale],
    ["Email", p.email],
    ["Telefono", p.telefono],
    ["Indirizzo", p.indirizzo],
    ["Città", p.citta],
    ["CAP", p.cap],
    ["Condizioni di pagamento", p.condizioni_pagamento ? paymentTermLabel(p.condizioni_pagamento) : "—"],
    ["Fido concesso", p.fido_concesso ? euro(p.fido_concesso) : "—"],
  ];

  return (
    <PortalLayout>
      <div className="relative overflow-hidden rounded-sm border border-border mb-8">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/80 backdrop-blur-md" />
        <div className="relative p-8">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Portale clienti</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1" data-testid="portal-client-name">
            {profile?.ragione_sociale || "—"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {profile?.indirizzo} {profile?.citta && `· ${profile.citta}`} {profile?.partita_iva && `· P.IVA ${profile.partita_iva}`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="rounded-sm border-border shadow-none p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Contratti attivi</div>
          <div className="font-heading text-3xl font-bold mt-2">{contracts.filter((c) => c.stato === "attivo").length}</div>
        </Card>
        <Card className="rounded-sm border-border shadow-none p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Dispositivi</div>
          <div className="font-heading text-3xl font-bold mt-2">{devices.length}</div>
        </Card>
        <Card className="rounded-sm border-border shadow-none p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Stato cliente</div>
          <div className="mt-3">{profile && <StatusBadge status={profile.stato} />}</div>
        </Card>
      </div>

      <Card className="rounded-sm border-border shadow-none p-6 mb-8" data-testid="portal-anagrafica">
        <h2 className="font-heading font-semibold mb-5">Anagrafica</h2>
        {(p.referenti || []).length > 0 && (
          <div className="mb-5 p-4 rounded-sm bg-secondary/50 border border-border">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Persona di riferimento</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {p.referenti.map((r, i) => (
                <div key={i} className="text-sm">
                  <div className="font-semibold">{r.nome || "—"}{r.ruolo ? <span className="text-muted-foreground font-normal"> · {r.ruolo}</span> : null}</div>
                  {r.telefono && <div className="text-muted-foreground">Cell. {r.telefono}</div>}
                  {r.email && <div className="text-muted-foreground">{r.email}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {rows.map(([label, val]) => (
            <div key={label}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
              <div className="text-sm mt-0.5">{val || "—"}</div>
            </div>
          ))}
        </div>
        {(p.domini || []).length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Domini</div>
            <div className="flex flex-wrap gap-1.5">{p.domini.map((d) => <Badge key={d} variant="outline" className="rounded-sm">{d}</Badge>)}</div>
          </div>
        )}
        {(p.sedi || []).length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Sedi secondarie</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {p.sedi.map((s, i) => (
                <div key={i} className="p-3 rounded-sm border border-border text-sm">
                  <div className="font-semibold">{s.nome || "Sede"}</div>
                  {s.indirizzo && <div className="text-muted-foreground">{s.indirizzo}</div>}
                  <div className="text-muted-foreground">{[s.cap, s.citta].filter(Boolean).join(" ")}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </PortalLayout>
  );
}

function ContractCard({ c }) {
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let active = true;
    if (c.has_pdf) {
      fetchAuthBlobUrl(`/portal/contracts/${c.id}/pdf`).then((u) => { if (active) setUrl(u); }).catch(() => {});
    }
    return () => { active = false; };
  }, [c.id, c.has_pdf]);

  const canOpen = c.has_pdf && url;

  return (
    <Card data-testid="portal-contract-card" className="rounded-sm border-border shadow-none overflow-hidden">
      <div className="relative bg-zinc-100 h-64">
        {canOpen ? (
          <button type="button" onClick={() => setOpen(true)} className="block w-full h-full cursor-zoom-in" title="Clicca per visualizzare il documento" data-testid="portal-contract-preview-link">
            <iframe title={c.nome} src={`${url}#toolbar=0&navpanes=0&view=FitH`} className="w-full h-full pointer-events-none" />
          </button>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            <FilePdf size={40} />
            <span className="text-xs mt-2">{c.has_pdf ? "Caricamento anteprima…" : "Nessun PDF allegato"}</span>
          </div>
        )}
        {/* overlay con nome contratto e date */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
          <div className="text-white font-heading font-semibold leading-tight">{c.nome}</div>
          <div className="text-white/80 text-xs mt-1 flex flex-wrap gap-x-4">
            <span>Attivazione: {fmtDate(c.data_inizio)}</span>
            <span>Scadenza: {fmtDate(c.data_scadenza)}</span>
          </div>
        </div>
        <div className="absolute top-3 left-3"><TypeBadge tipo={c.tipo} /></div>
      </div>
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <StatusBadge status={c.stato} />
          {c.rinnovo_automatico
            ? <span className="inline-flex items-center gap-1 text-green-700 text-sm font-semibold" data-testid="contract-autorenew-on"><CheckCircle size={18} weight="fill" /> Rinnovo automatico</span>
            : <span className="inline-flex items-center gap-1 text-muted-foreground text-xs" data-testid="contract-autorenew-off"><XCircle size={16} /> Rinnovo automatico disattivato</span>}
        </div>
        {canOpen && (
          <button type="button" onClick={() => setOpen(true)} className="text-sm text-primary font-medium hover:underline flex items-center gap-1" data-testid="portal-contract-open-pdf">
            <FilePdf size={16} /> Apri PDF
          </button>
        )}
      </div>

      {/* Anteprima documento in sovrimpressione (stessa pagina) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-5xl w-[92vw] p-0 gap-0 overflow-hidden" data-testid="portal-contract-preview-dialog">
          <DialogHeader className="px-5 py-3 border-b border-border">
            <DialogTitle className="font-heading text-base flex items-center gap-2 pr-8 truncate"><FilePdf size={18} className="text-primary shrink-0" /> <span className="truncate">{c.nome}</span></DialogTitle>
          </DialogHeader>
          {url && <iframe title={`${c.nome} — anteprima`} src={url} className="w-full h-[78vh] bg-muted" data-testid="portal-contract-preview-iframe" />}
          <DialogFooter className="px-5 py-3 border-t border-border">
            <Button className="rounded-sm" onClick={() => setOpen(false)} data-testid="portal-contract-preview-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function PortalContracts() {
  const [contracts, setContracts] = useState(null);
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/portal/contracts").then(({ data }) => setContracts(data));
    api.get("/portal/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);
  const svcLabel = (s) => (s.fornitore === "Sophos" ? "Sophos" : s.fornitore === "Microsoft CSP" ? "Microsoft 365 / Office 365" : (s.nome || s.tipo));
  return (
    <PortalLayout>
      <H title="I tuoi contratti" subtitle="Anteprima dei documenti contrattuali e servizi attivi" />
      {contracts?.length === 0 && <p className="text-center text-muted-foreground py-6">Nessun contratto disponibile.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contracts?.map((c) => <ContractCard key={c.id} c={c} />)}
      </div>

      {services.length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading text-xl font-bold mb-1">Servizi attivi</h2>
          <p className="text-sm text-muted-foreground mb-4">Domini, licenze e servizi associati al tuo account</p>
          <Card className="rounded-sm border-border shadow-none">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Servizio</TableHead><TableHead>Tipo</TableHead><TableHead className="text-center">Q.tà</TableHead>
                <TableHead>Scadenza</TableHead><TableHead className="text-right">Licenze</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {services.map((s) => (
                  <TableRow key={s.id} data-testid="portal-service-row">
                    <TableCell className="font-medium">{svcLabel(s)}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm">{s.tipo || "—"}</Badge></TableCell>
                    <TableCell className="text-center tabular-nums">{s.quantita ?? 1}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{s.data_scadenza ? fmtDate(s.data_scadenza) : "—"}</TableCell>
                    <TableCell className="text-right">
                      {s.numero_licenze != null ? <span className="font-semibold tabular-nums">{s.numero_licenze}</span> : <span className="text-muted-foreground text-sm">—</span>}
                      {s.fornitore !== "Sophos" && (s.licenze_tipi || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 justify-end mt-1">
                          {s.licenze_tipi.slice(0, 6).map((t, i) => <Badge key={i} variant="outline" className="rounded-sm text-[10px]">{t}</Badge>)}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </PortalLayout>
  );
}

export function PortalDocuments() {
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(false);
  const [nome, setNome] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/portal/documents").then(({ data }) => setDocs(data));
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file PDF"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (nome.trim()) fd.append("nome", nome.trim());
    try {
      await api.post("/portal/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setOpen(false); setNome(""); setFile(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <PortalLayout>
      <div className="flex items-start justify-between">
        <H title="Documentazione" subtitle="Documenti condivisi con te e file che carichi tu" />
        <Button data-testid="portal-upload-doc-btn" onClick={() => setOpen(true)} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Carica PDF</Button>
      </div>
      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Documento</TableHead><TableHead>Categoria</TableHead><TableHead>Origine</TableHead>
            <TableHead>Data</TableHead><TableHead className="text-right">Apri</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs?.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nessun documento disponibile.</TableCell></TableRow>}
            {docs?.map((d) => (
              <TableRow key={d.id} data-testid="portal-doc-row" className="hover:bg-secondary">
                <TableCell className="font-medium flex items-center gap-2"><FilePdf size={16} className="text-muted-foreground" /> {d.filename}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{d.categoria}</Badge></TableCell>
                <TableCell>{d.uploaded_by_client ? <Badge variant="outline" className="rounded-sm text-blue-700">Caricato da te</Badge> : <span className="text-sm text-muted-foreground">Condiviso</span>}</TableCell>
                <TableCell className="tabular-nums text-muted-foreground">{fmtDate(d.uploaded_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => openAuthPdf(d.download_url)} title="Apri"><FilePdf size={16} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Carica un documento</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase tracking-wider">Nome documento</Label><Input data-testid="portal-doc-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Documento identità" className="mt-1 rounded-sm" /></div>
            <DropZone onFiles={(fl) => setFile(fl?.[0] || null)} className="block"><Label className="text-xs uppercase tracking-wider">File PDF *</Label><Input data-testid="portal-doc-file" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 rounded-sm" /></DropZone>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button><Button data-testid="portal-doc-save-btn" onClick={upload} disabled={uploading} className="rounded-sm">{uploading ? "Caricamento..." : "Carica"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}

const RIP_STATO = { accettato: "Accettato", in_lavorazione: "In lavorazione", in_attesa_ricambi: "In attesa ricambi", riparato: "Riparato", non_riparabile: "Non riparabile", consegnato: "Consegnato", reso_non_riparato: "Reso non riparato" };

export function PortalRepairs() {
  const [repairs, setRepairs] = useState(null);
  useEffect(() => { api.get("/portal/repairs").then(({ data }) => setRepairs(data)); }, []);
  return (
    <PortalLayout>
      <H title="Le tue riparazioni" subtitle="Stato dei tuoi dispositivi in riparazione e report" />
      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Numero</TableHead><TableHead>Dispositivo</TableHead><TableHead>Problema</TableHead>
            <TableHead>Stato</TableHead><TableHead>Accettazione</TableHead><TableHead className="text-right">Report</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {repairs?.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nessuna riparazione.</TableCell></TableRow>}
            {repairs?.map((r) => (
              <TableRow key={r.id} data-testid="portal-repair-row" className="hover:bg-secondary">
                <TableCell className="font-mono text-xs font-medium">{r.numero}</TableCell>
                <TableCell className="font-medium">{[r.tipo_dispositivo, r.marca, r.modello].filter(Boolean).join(" ")}</TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">{r.problema_segnalato || "—"}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{RIP_STATO[r.stato] || r.stato}</Badge></TableCell>
                <TableCell className="tabular-nums text-muted-foreground">{r.data_accettazione ? fmtDate(r.data_accettazione) : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => openAuthPdf(`/portal/riparazioni/${r.id}/scheda-pdf`)} data-testid="portal-repair-report-btn" title="Apri scheda"><FilePdf size={16} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </PortalLayout>
  );
}

export function PortalSupport() {
  const [contracts, setContracts] = useState(null);
  const [selId, setSelId] = useState("");
  const [statement, setStatement] = useState(null);

  useEffect(() => {
    api.get("/portal/support-contracts").then(({ data }) => {
      const mo = (data || []).filter((c) => c.tipo === "monte_ore");
      setContracts(mo);
      if (mo.length) setSelId(mo[0].id);
    }).catch(() => setContracts([]));
  }, []);

  useEffect(() => {
    if (selId) api.get(`/portal/support-contracts/${selId}/statement`).then(({ data }) => setStatement(data)).catch(() => setStatement(null));
  }, [selId]);

  const sel = (contracts || []).find((c) => c.id === selId);

  return (
    <PortalLayout>
      <H title="Monte ore assistenza" subtitle="Estratto conto delle ore acquistate e utilizzate" />
      {sel && (
        <div className="mb-4 text-sm text-muted-foreground" data-testid="portal-monteore-contract-label">
          Contratto <span className="font-semibold text-foreground">{sel.numero}</span>{sel.oggetto ? ` · ${sel.oggetto}` : ""}
        </div>
      )}
      {contracts && contracts.length === 0 && (
        <Card className="rounded-sm border-border shadow-none p-10 text-center text-muted-foreground" data-testid="portal-no-monteore">
          Non hai contratti a monte ore attivi.
        </Card>
      )}
      {contracts && contracts.length > 0 && (
        <>
          {contracts.length > 1 && (
            <div className="mb-6 max-w-sm">
              <Label className="text-xs uppercase tracking-wider">Contratto</Label>
              <Select value={selId} onValueChange={setSelId}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="portal-monteore-select"><SelectValue /></SelectTrigger>
                <SelectContent>{contracts.map((c) => <SelectItem key={c.id} value={c.id}>{c.numero} · {c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="rounded-sm border-border shadow-none p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Ore acquistate</div><div className="font-heading text-2xl font-bold mt-2">{sel?.ore_acquistate || 0}h</div></Card>
            <Card className="rounded-sm border-border shadow-none p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Ore utilizzate</div><div className="font-heading text-2xl font-bold mt-2">{sel?.ore_utilizzate || 0}h</div></Card>
            <Card className="rounded-sm border-primary shadow-none p-5 bg-primary/5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Ore residue</div><div className="font-heading text-2xl font-bold mt-2 text-primary" data-testid="portal-monteore-residue">{sel?.ore_residue || 0}h</div></Card>
            <Card className="rounded-sm border-border shadow-none p-5"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Scadenza</div><div className="font-heading text-lg font-bold mt-2">{sel?.data_scadenza ? fmtDate(sel.data_scadenza) : "—"}</div></Card>
          </div>

          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-semibold">Estratto conto</h2>
            {sel && <Button variant="outline" size="sm" className="rounded-sm gap-1" data-testid="portal-estratto-pdf-btn" onClick={() => openAuthPdf(`/support-contracts/${sel.id}/statement.pdf`)}><FilePdf size={14} /> Scarica PDF</Button>}
          </div>
          <Card className="rounded-sm border-border shadow-none">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Codice</TableHead><TableHead>Data</TableHead><TableHead>Descrizione</TableHead>
                <TableHead className="text-right">Accrediti</TableHead><TableHead className="text-right">Addebiti</TableHead><TableHead className="text-right">Saldo</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(!statement || statement.righe.length === 0) && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nessun movimento registrato.</TableCell></TableRow>}
                {statement?.righe.map((r, i) => (
                  <TableRow key={i} data-testid="portal-estratto-row" className="hover:bg-secondary">
                    <TableCell className="font-mono text-xs">{r.codice}</TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">{fmtDate(r.data)}</TableCell>
                    <TableCell className="max-w-[240px] truncate">{r.descrizione}</TableCell>
                    <TableCell className="text-right tabular-nums text-green-700 font-medium">{r.tipo === "accredito" ? `+${r.ore}h` : ""}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-700 font-medium">{r.tipo === "addebito" ? `−${r.ore}h` : (r.tipo === "garanzia" ? "garanzia" : (r.tipo === "previsto" ? <span className="text-amber-600">{r.ore}h previsto</span> : ""))}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{r.saldo}h</TableCell>
                  </TableRow>
                ))}
                {statement && statement.righe.length > 0 && (
                  <TableRow className="bg-secondary/60 font-semibold">
                    <TableCell colSpan={3} className="text-right">Totali</TableCell>
                    <TableCell className="text-right tabular-nums text-green-700">+{statement.totale_accrediti_ore}h</TableCell>
                    <TableCell className="text-right tabular-nums text-red-700">−{statement.totale_addebiti_ore}h</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid="portal-estratto-saldo">{statement.saldo_ore}h</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </PortalLayout>
  );
}


export function PortalDevices() {
  const [devices, setDevices] = useState(null);
  const [open, setOpen] = useState(false);
  const [readings, setReadings] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => { api.get("/portal/devices").then(({ data }) => setDevices(data)); }, []);

  const viewReadings = async (d) => {
    setCurrent(d);
    const { data } = await api.get(`/portal/devices/${d.id}/readings`);
    setReadings(data); setOpen(true);
  };

  const downloadReadingAllegato = async (r) => {
    try {
      const res = await api.get(`/portal/readings/${r.id}/allegato/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = r.allegato?.nome_originale || "allegato";
      link.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore durante il download dell'allegato"); }
  };

  return (
    <PortalLayout>
      <H title="Fotocopiatrici" subtitle="Letture e consumi delle macchine Ricoh" />
      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispositivo</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Ubicazione</TableHead>
              <TableHead>Ultima lettura</TableHead>
              <TableHead>Contatori</TableHead>
              <TableHead className="text-right">Letture</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices?.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Nessun dispositivo.</TableCell></TableRow>}
            {devices?.map((d) => (
              <TableRow key={d.id} data-testid="portal-device-row">
                <TableCell className="font-medium">{d.marca} {d.modello}</TableCell>
                <TableCell className="font-mono text-xs">{d.serial || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{d.ubicazione || "—"}</TableCell>
                <TableCell className="tabular-nums">{d.ultima_lettura ? fmtDate(d.ultima_lettura.data_lettura) : "—"}</TableCell>
                <TableCell className="tabular-nums text-sm">{d.ultima_lettura ? <>B/N {d.ultima_lettura.contatore_bn} · Col {d.ultima_lettura.contatore_colore}</> : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => viewReadings(d)}><Gauge size={16} weight="bold" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Storico letture — {current?.marca} {current?.modello}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Contatore B/N</TableHead>
                <TableHead className="text-right">Contatore colore</TableHead>
                <TableHead className="text-right">Allegato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {readings.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nessuna lettura.</TableCell></TableRow>}
              {readings.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="tabular-nums">{fmtDate(r.data_lettura)}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.contatore_bn}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.contatore_colore}</TableCell>
                  <TableCell className="text-right">
                    {r.allegato ? <Button variant="ghost" size="icon" onClick={() => downloadReadingAllegato(r)} className="h-8 w-8 text-primary" title={r.allegato.nome_originale} data-testid="portal-reading-allegato-download"><Paperclip size={14} /></Button> : <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}

export function PortalReports() {
  const [reports, setReports] = useState(null);
  useEffect(() => { api.get("/portal/reports").then(({ data }) => setReports(data)); }, []);
  return (
    <PortalLayout>
      <H title="Report di consumo" subtitle="Riepiloghi periodici dei tuoi servizi" />
      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Periodo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Totale</TableHead>
              <TableHead>Generato</TableHead>
              <TableHead className="text-right">Download</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports?.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nessun report disponibile.</TableCell></TableRow>}
            {reports?.map((r) => (
              <TableRow key={r.id} data-testid="portal-report-row">
                <TableCell className="tabular-nums text-sm">{r.period_start} → {r.period_end}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm capitalize">{r.period_type}</Badge></TableCell>
                <TableCell className="text-right tabular-nums font-medium">{euro(r.totale)}</TableCell>
                <TableCell className="tabular-nums text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {r.files?.csv && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadReport(r.id, "csv", "report")}><FileCsv size={18} /></Button>}
                    {r.files?.xlsx && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadReport(r.id, "xlsx", "report")}><FileXls size={18} /></Button>}
                    {r.files?.pdf && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadReport(r.id, "pdf", "report")}><FilePdf size={18} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </PortalLayout>
  );
}


export function PortalTickets() {
  const [tickets, setTickets] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ oggetto: "", descrizione: "" });
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/portal/tickets").then(({ data }) => setTickets(data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  const HdBadge = ({ item, fallback = "—" }) => item ? (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border"
      style={{ backgroundColor: (item.colore || "#94a3b8") + "20", color: item.colore || "#64748b", borderColor: (item.colore || "#94a3b8") + "55" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.colore || "#94a3b8" }} />{item.nome}
    </span>
  ) : <span className="text-xs text-muted-foreground">{fallback}</span>;

  const create = async () => {
    if (!form.oggetto.trim()) { toast.error("Inserisci l'oggetto"); return; }
    setSaving(true);
    try {
      await api.post("/portal/tickets", form);
      toast.success("Ticket inviato"); setOpenNew(false); setForm({ oggetto: "", descrizione: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setSaving(false); }
  };
  const openDetail = async (t) => { try { const { data } = await api.get(`/portal/tickets/${t.id}`); setDetail(data); setReply(""); } catch { toast.error("Errore"); } };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { const { data } = await api.post(`/portal/tickets/${detail.id}/reply`, { testo: reply }); setDetail(data); setReply(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <PortalLayout>
      <div className="flex items-start justify-between">
        <H title="I miei ticket" subtitle="Apri richieste di assistenza e segui le risposte del nostro team" />
        <Button data-testid="portal-new-ticket-btn" onClick={() => setOpenNew(true)} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Nuovo ticket</Button>
      </div>
      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Numero</TableHead><TableHead>Oggetto</TableHead><TableHead>Priorità</TableHead><TableHead>Stato</TableHead><TableHead>Aggiornato</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tickets?.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Nessun ticket. Apri la tua prima richiesta.</TableCell></TableRow>}
            {tickets?.map((t) => (
              <TableRow key={t.id} data-testid="portal-ticket-row" className="hover:bg-secondary cursor-pointer" onClick={() => openDetail(t)}>
                <TableCell className="font-mono text-xs font-medium">{t.numero_ticket}</TableCell>
                <TableCell className="font-medium max-w-xs truncate">{t.oggetto}</TableCell>
                <TableCell><HdBadge item={t.priorita} /></TableCell>
                <TableCell><HdBadge item={t.stato} /></TableCell>
                <TableCell className="tabular-nums text-muted-foreground">{fmtDate(t.data_ultimo_aggiornamento)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nuovo ticket</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase tracking-wider">Oggetto *</Label><Input data-testid="portal-ticket-oggetto" value={form.oggetto} onChange={(e) => setForm((f) => ({ ...f, oggetto: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Descrizione</Label><Textarea data-testid="portal-ticket-desc" value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} className="mt-1 rounded-sm" rows={4} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-sm">Annulla</Button><Button data-testid="portal-save-ticket-btn" onClick={create} disabled={saving} className="rounded-sm">{saving ? "Invio…" : "Invia"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="portal-ticket-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{detail.numero_ticket} · {detail.oggetto}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">Stato: <HdBadge item={detail.stato} /> Priorità: <HdBadge item={detail.priorita} /></div>
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="portal-thread">
                {(detail.conversazione || []).length === 0 && <p className="text-sm text-muted-foreground">Nessun messaggio.</p>}
                {(detail.conversazione || []).map((m) => (
                  <div key={m.id} className={`text-sm p-3 rounded-sm ${m.tipo === "cliente" ? "bg-secondary" : "bg-blue-50 border border-blue-100"}`}>
                    <div className="text-xs text-muted-foreground mb-1"><span className="font-medium">{m.tipo === "cliente" ? m.autore : "Team assistenza"}</span> · {fmtDate(m.data)}</div>
                    <div className="whitespace-pre-wrap">{m.testo}</div>
                  </div>
                ))}
              </div>
              {!(detail.stato?.blocca_modifiche) ? (
                <div className="flex gap-2">
                  <Input data-testid="portal-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Scrivi una risposta..." className="rounded-sm" onKeyDown={(e) => e.key === "Enter" && sendReply()} />
                  <Button data-testid="portal-send-reply-btn" onClick={sendReply} className="rounded-sm gap-2"><ChatCircleDots size={15} /> Invia</Button>
                </div>
              ) : <p className="text-xs text-muted-foreground border-t border-border pt-3">Questo ticket è chiuso. Apri un nuovo ticket per ulteriori richieste.</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}


const PREV_ST_COLOR = {
  "Bozza": "bg-secondary text-muted-foreground border-border", "Inviato al cliente": "bg-blue-50 text-blue-700 border-blue-200",
  "In trattativa": "bg-purple-50 text-purple-700 border-purple-200", "Accettato": "bg-green-50 text-green-700 border-green-200",
  "Perso": "bg-rose-50 text-rose-700 border-rose-200", "Scaduto": "bg-amber-50 text-amber-700 border-amber-200",
};

export function PortalPreventivi() {
  const [items, setItems] = useState(null);
  const [dlg, setDlg] = useState(null); // preventivo selezionato per accettazione
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/cliente/preventivi").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openAccept = (p) => { setDlg(p); setChecked(false); };
  const confirmAccept = async () => {
    if (!checked || !dlg) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cliente/preventivi/${dlg.id}/accetta`, { conferma: true });
      if (data.already) toast.info("Preventivo già accettato");
      else toast.success("Preventivo accettato correttamente. Riceverai una conferma via e-mail.");
      setDlg(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <PortalLayout>
      <H title="Preventivi" subtitle="Consulta e accetta i preventivi che ti abbiamo condiviso" />
      <Card className="rounded-sm border-border shadow-none" data-testid="portal-preventivi">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Numero</TableHead><TableHead>Oggetto</TableHead><TableHead>Data</TableHead>
            <TableHead>Scadenza</TableHead><TableHead className="text-right">Importo</TableHead>
            <TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items === null ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Caricamento…</TableCell></TableRow>
              : items.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground" data-testid="portal-prev-empty">Nessun preventivo disponibile.</TableCell></TableRow>
              : items.map((p) => (
                <TableRow key={p.id} data-testid="portal-prev-row">
                  <TableCell className="font-medium">{p.numero_preventivo || "—"}</TableCell>
                  <TableCell>{p.oggetto || "—"}</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">{fmtDate(p.data_preventivo) || "—"}</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">{fmtDate(p.data_scadenza) || "—"}{p.scaduto && <Badge variant="outline" className="ml-2 rounded-sm bg-amber-50 text-amber-700 border-amber-200" data-testid="portal-prev-scaduto">Scaduto</Badge>}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{euro(p.importo)}</TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-sm ${PREV_ST_COLOR[p.stato] || "bg-secondary"}`}>{p.stato}</Badge></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="rounded-sm gap-1 mr-2" data-testid="portal-prev-pdf" onClick={() => openAuthPdf(`/cliente/preventivi/${p.id}/pdf`)}><FilePdf size={15} /> PDF</Button>
                    {p.can_accept
                      ? <Button size="sm" className="rounded-sm gap-1" data-testid="portal-prev-accept-btn" onClick={() => openAccept(p)}><CheckCircle size={15} /> Accetta</Button>
                      : p.scaduto
                        ? <span className="text-xs text-amber-700" data-testid="portal-prev-scaduto-msg">Preventivo scaduto, contattaci per una proposta aggiornata.</span>
                        : p.stato === "Accettato"
                          ? <span className="text-xs text-green-700 inline-flex items-center gap-1" data-testid="portal-prev-accepted"><CheckCircle size={14} weight="fill" /> Accettato</span>
                          : null}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="portal-accept-dialog">
          <DialogHeader><DialogTitle className="font-heading">Accettazione preventivo</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-4">
              <div className="rounded-sm border border-border p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Numero</span><span className="font-medium">{dlg.numero_preventivo || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Oggetto</span><span className="font-medium">{dlg.oggetto || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Importo</span><span className="font-medium tabular-nums">{euro(dlg.importo)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Data preventivo</span><span className="tabular-nums">{fmtDate(dlg.data_preventivo) || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Data scadenza</span><span className="tabular-nums">{fmtDate(dlg.data_scadenza) || "—"}</span></div>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1" data-testid="portal-accept-checkbox" />
                <span>Dichiaro di aver preso visione del preventivo allegato e di accettarne il contenuto.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(null)}>Annulla</Button>
            <Button onClick={confirmAccept} disabled={!checked || busy} className="rounded-sm" data-testid="portal-accept-confirm-btn">{busy ? "Invio…" : "Conferma accettazione"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}



export function PortalLavorazioni() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/client-area/lavorazioni").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);
  const faseBadge = (x) => <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border" style={{ backgroundColor: (x.fase_color || "#94a3b8") + "20", color: x.fase_color || "#64748b", borderColor: (x.fase_color || "#94a3b8") + "55" }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: x.fase_color || "#94a3b8" }} />{x.fase_label}</span>;
  return (
    <PortalLayout>
      <H title="Lavorazioni" subtitle="Stato di avanzamento delle lavorazioni presso la tua azienda" />
      {items === null ? <p className="text-sm text-muted-foreground">Caricamento…</p> :
        items.length === 0 ? <Card className="rounded-sm border-border shadow-none p-10 text-center text-muted-foreground" data-testid="portal-lav-empty">Nessuna lavorazione da mostrare.</Card> :
          <div className="space-y-4">
            {items.map((x) => (
              <Card key={x.id} className="rounded-sm border-border shadow-none p-5" data-testid={`portal-lav-${x.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-mono text-muted-foreground">{x.numero_lavorazione}</div>
                    <h3 className="font-heading font-semibold text-base">{x.titolo_lavorazione}</h3>
                    {x.descrizione_lavorazione && <p className="text-sm text-muted-foreground mt-1">{x.descrizione_lavorazione}</p>}
                  </div>
                  {faseBadge(x)}
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                  {x.data_prevista && <span>Data prevista: <b className="text-foreground">{fmtDate(x.data_prevista)}</b></span>}
                  {x.stato_merce && <span>Merce: <b className="text-foreground">{x.stato_merce}</b></span>}
                  {(x.allegati_nomi || []).length > 0 && <span>Documenti: {x.allegati_nomi.join(", ")}</span>}
                </div>
                {(x.sottolavorazioni || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Sotto-lavorazioni</div>
                    <div className="space-y-2">
                      {x.sottolavorazioni.map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-2" data-testid={`portal-sublav-${s.id}`}>
                          <div>
                            <span className="text-sm font-medium">{s.titolo_lavorazione}</span>
                            {s.data_prevista && <span className="text-xs text-muted-foreground ml-2">{fmtDate(s.data_prevista)}</span>}
                          </div>
                          {faseBadge(s)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>}
    </PortalLayout>
  );
}



export function PortalDispositivi() {
  const [zyxel, setZyxel] = useState([]);
  const [ninja, setNinja] = useState([]);
  useEffect(() => { api.get("/portal/zyxel-devices").then(({ data }) => setZyxel(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/portal/ninja-devices").then(({ data }) => setNinja(data)).catch(() => {}); }, []);
  const catLabel = { ap: "Access Point", sw: "Switch", gw: "Gateway / Firewall" };
  return (
    <PortalLayout>
      <H title="Dispositivi" subtitle="Apparati di rete e postazioni gestite (sola lettura)" />
      {zyxel.length === 0 && ninja.length === 0 && (
        <Card className="rounded-sm border-border shadow-none p-10 text-center text-muted-foreground" data-testid="portal-dispositivi-empty">Nessun dispositivo disponibile.</Card>
      )}
      {zyxel.length > 0 && (
        <Card className="rounded-sm border-border shadow-none" data-testid="portal-zyxel">
          <div className="px-5 pt-5 pb-1">
            <h3 className="font-heading font-semibold">Rete Zyxel</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Stato dei tuoi apparati di rete (sola lettura).</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispositivo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Sede</TableHead>
                <TableHead>Firmware</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zyxel.map((d, i) => (
                <TableRow key={i} data-testid="portal-zyxel-row">
                  <TableCell className="font-medium">{d.name || d.model || "—"}<div className="text-xs text-muted-foreground">{d.model}</div></TableCell>
                  <TableCell className="text-sm">{catLabel[d.category] || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.site_name || "—"}</TableCell>
                  <TableCell className="text-sm">{d.firmware || "—"}</TableCell>
                  <TableCell><span className={`inline-flex items-center gap-1.5 text-xs font-medium ${d.online ? "text-emerald-600" : "text-slate-500"}`}>● {d.online ? "Online" : "Offline"}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {ninja.length > 0 && (
        <Card className="rounded-sm border-border shadow-none mt-6" data-testid="portal-ninja">
          <div className="px-5 pt-5 pb-1">
            <h3 className="font-heading font-semibold">Dispositivi gestiti (NinjaOne)</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Postazioni e server monitorati (sola lettura).</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Sistema operativo</TableHead>
                <TableHead>Modello</TableHead>
                <TableHead>Ultimo contatto</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ninja.map((d, i) => (
                <TableRow key={i} data-testid="portal-ninja-row">
                  <TableCell className="font-medium">{d.system_name || d.display_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.node_class || "—"}</TableCell>
                  <TableCell className="text-sm">{d.os_name || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{[d.manufacturer, d.model].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell className="tabular-nums text-sm">{d.last_contact ? fmtDate(d.last_contact) : "—"}</TableCell>
                  <TableCell><span className={`inline-flex items-center gap-1.5 text-xs font-medium ${!d.offline ? "text-emerald-600" : "text-slate-500"}`}>● {!d.offline ? "Online" : "Offline"}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </PortalLayout>
  );
}

export function PortalIso27001() {
  const lang = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/iso27001").then(({ data }) => setData(data)).catch(() => setData({ visible: false })); }, []);
  const STL = { non_avviato: "Non avviato", in_corso: "In corso", attuato: "Attuato" };
  const STC = { non_avviato: "bg-rose-50 text-rose-700 border-rose-200", in_corso: "bg-amber-50 text-amber-700 border-amber-200", attuato: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  const THEME_COLORS = { organizational: "#00396a", people: "#0b8457", physical: "#d97706", technological: "#7c3aed" };
  const themes = data?.themes || {};
  const editable = !!data?.puo_modificare;
  const patch = async (code, partial) => {
    setData((d) => ({ ...d, controls: d.controls.map((c) => (c.code === code ? { ...c, ...partial } : c)) }));
    try { const { data: r } = await api.patch(`/portal/iso27001/controls/${code}`, partial); setData((d) => ({ ...d, compliance: r.compliance, compliance_by_theme: r.compliance_by_theme })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const kpis = data?.visible ? [["Conformità", `${data.compliance.pct}%`], ["Applicabili", data.compliance.applicabili], ["Attuati", data.compliance.attuati], ["In corso", data.compliance.in_corso], ["Non avviati", data.compliance.non_avviato]] : [];
  return (
    <PortalLayout>
      <H title="Sicurezza ISO 27001" subtitle="Dichiarazione di Applicabilità (Annex A) dei controlli di sicurezza" />
      {data && !data.visible && (
        <Card className="rounded-sm border-border shadow-none p-10 text-center text-muted-foreground" data-testid="portal-iso-empty">
          {translate("La Dichiarazione di Applicabilità non è ancora disponibile.", lang)}
        </Card>
      )}
      {data?.visible && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            {kpis.map(([l, v]) => (
              <Card key={l} className="rounded-sm border-border shadow-none p-4"><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{translate(l, lang)}</div><div className="font-heading text-2xl font-bold mt-1 tabular-nums">{v}</div></Card>
            ))}
          </div>
          {data.compliance_by_theme && (
            <Card className="rounded-sm border-border shadow-none p-4 mb-6" data-testid="portal-iso-donut">
              <div className="text-sm font-semibold mb-2">{translate("Conformità per tema", lang)}</div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={data.compliance_by_theme} dataKey="pct" nameKey="label" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {data.compliance_by_theme.map((t) => <Cell key={t.theme} fill={THEME_COLORS[t.theme] || "#64748b"} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={{ background: "#09090B", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          )}
          <div className="flex justify-end mb-4">
            <Button onClick={() => openAuthPdf("/portal/iso27001/pdf")} className="rounded-sm gap-2" data-testid="portal-iso-pdf"><FilePdf size={16} /> {translate("Scarica PDF", lang)}</Button>
          </div>
          <Card className="rounded-sm border-border shadow-none">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-24">{translate("Controllo", lang)}</TableHead><TableHead>{translate("Titolo", lang)}</TableHead>
                <TableHead className="text-center w-28">{translate("Applicabile", lang)}</TableHead><TableHead className="w-40">{translate("Stato", lang)}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.controls.map((c, i) => {
                  const prev = data.controls[i - 1];
                  const showTheme = !prev || prev.theme !== c.theme;
                  return (
                    <>
                      {showTheme && <TableRow key={`t-${c.theme}`} className="bg-secondary/60"><TableCell colSpan={4} className="font-semibold text-primary text-xs uppercase tracking-wider">{themes[c.theme]?.[lang] || themes[c.theme]?.it || c.theme}</TableCell></TableRow>}
                      <TableRow key={c.code} data-testid="portal-iso-row">
                        <TableCell className="font-medium tabular-nums">A.{c.code}</TableCell>
                        <TableCell className="text-sm">{lang === "en" ? c.title_en : c.title_it}{editable && <input defaultValue={c.giustificazione || ""} onBlur={(e) => { if (e.target.value !== (c.giustificazione || "")) patch(c.code, { giustificazione: e.target.value }); }} placeholder={translate("Note", lang)} className="mt-1 w-full border border-border rounded-sm px-2 py-1 h-7 text-xs" data-testid={`portal-iso-note-${c.code}`} />}</TableCell>
                        <TableCell className="text-center">{editable ? <Switch checked={!!c.applicabile} onCheckedChange={(v) => patch(c.code, { applicabile: v })} data-testid={`portal-iso-app-${c.code}`} /> : (c.applicabile ? translate("Sì", lang) : "—")}</TableCell>
                        <TableCell>{!c.applicabile ? <Badge variant="outline" className="rounded-sm text-muted-foreground">{translate("Escluso", lang)}</Badge> : editable ? <Select value={c.stato} onValueChange={(v) => patch(c.code, { stato: v })}><SelectTrigger className={`h-8 rounded-sm text-xs ${STC[c.stato]}`} data-testid={`portal-iso-stato-${c.code}`}><SelectValue /></SelectTrigger><SelectContent>{["non_avviato", "in_corso", "attuato"].map((s) => <SelectItem key={s} value={s}>{translate(STL[s], lang)}</SelectItem>)}</SelectContent></Select> : <Badge variant="outline" className={`rounded-sm ${STC[c.stato]}`}>{translate(STL[c.stato], lang)}</Badge>}</TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </PortalLayout>
  );
}